import { Center, Box, BoxProps } from '@chakra-ui/react'
import { useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import * as React from 'react'

import { TextBodyCopy, TextHeadline1, TextSubtitleEn } from '../common/Text'
import { useResponsive } from '../../hooks/useResponsive'
import { vw } from '../../styles/theme'
import { MotionBox } from '../common/MotionBox'
import useLocale from '../../hooks/useLocale'

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25,
    },
  },
}
const item = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'tween',
      ease: 'easeOut',
      duration: 0.5,
    },
  },
}

export type OverviewProps = BoxProps & {
  head1Key: string
  head2Key: string
  bodyKey: string
}

const Overview: React.FC<OverviewProps> = ({ head1Key, head2Key, bodyKey, ...others }) => {
  const { isJa, locale } = useLocale()
  const isPC = useResponsive(false, true)

  const { inView, ref } = useInView({ threshold: 0.25, triggerOnce: true })
  const controls = useAnimation()

  React.useEffect(async () => {
    if (inView) {
      await controls.start('visible')
    }
  }, [controls, inView])

  React.useEffect(async () => {
    if (inView) {
      await controls.start('visible', { duration: 0 })
    }
  }, [locale])

  return (
    <>
      <Box
        ref={ref}
        minW={['contentMinWSP', 'contentMinWPC']}
        w={['contentSP', '100%']}
        h="100vh"
        m={['0 auto', 'inherit']}
        {...others}
      >
        <Center w="100%" h="100%">
          {isJa && isPC ? (
            <Box
              css={{
                msWritingMode: 'tb-rl',
                writingMode: 'vertical-rl',
                span: {
                  textCombineUpright: 'all',
                },
              }}
            >
              <MotionBox variants={container} initial="hidden" animate={controls}>
                <MotionBox variants={item}>
                  <TextHeadline1 as="h1" localizeKey={head1Key} lineHeight="1" mt="-0.2em" ml="60px" />
                </MotionBox>
                <MotionBox variants={item}>
                  <TextSubtitleEn as="h2" localizeKey={head2Key} lineHeight="1" ml="40px" />
                </MotionBox>
                <MotionBox variants={item}>
                  <TextBodyCopy as="p" localizeKey={bodyKey} />
                </MotionBox>
              </MotionBox>
            </Box>
          ) : (
            <MotionBox variants={container} initial="hidden" animate={controls}>
              <Box textAlign={['center', 'left']} maxW={['auto', '800px']}>
                <MotionBox variants={item}>
                  <TextHeadline1 as="h1" localizeKey={head1Key} lineHeight="1" mb={[vw(60), '60px']} />
                </MotionBox>
                <MotionBox variants={item}>
                  <TextSubtitleEn as="h2" localizeKey={head2Key} lineHeight="1" mb={[vw(50), '40px']} />
                </MotionBox>
                <MotionBox variants={item}>
                  <TextBodyCopy as="p" localizeKey={bodyKey} />
                </MotionBox>
              </Box>
            </MotionBox>
          )}
        </Center>
      </Box>
    </>
  )
}

export default Overview
